import { action, makeObservable, observable } from "mobx";
import { IPromiseBasedObservable, fromPromise } from "mobx-utils";
import {
  AdjustOutsideAppointmentsDto,
  AppointmentApi,
  AppointmentDto,
  AppointmentQueryFilters,
  AppointmentTemplateDto,
  AppointmentSchedulingParametersDto,
  BatchCancelDeleteAppointmentDto,
  CAResponse,
  ContinuousAppointmentDto,
  GeneratedWorkflowDto,
  PatientAvailabilityDto,
  ReschedulingRequestDto,
  SalesTemplateDto,
  SolutionDto,
  TimeSlotDto,
  UpdateAppointmentDto,
  UpdateCancelledAppointmentDto,
  WaitListFilters,
} from "../../api-client";
import AuthStore from "../Auth/authStore";
import UserStore from "../User/UserStore";
import notificationStore from "../Notification/notificationStore";
import PatientsStore from "../Patients/patientsStore";

export class AgentParemetersDto {
  searchFunction: string = "beamSearch";
  pruningTarget: number = 4;
  betaTherapists: number = 20;
  betaTreatmentMonths: number = 15;
  betaDrivingTime: number = 1;
}

export default class AppointmentStore {
  public apiClient: AppointmentApi;
  public userStore: UserStore; // Include UserStore as a property
  public authStore: AuthStore;
  public patientStore: PatientsStore;

  public constructor(
    apiClient: AppointmentApi,
    userStore: UserStore,
    authStore: AuthStore,
    patientStore: PatientsStore,
  ) {
    makeObservable(this);
    this.apiClient = apiClient;
    this.userStore = userStore; // Assign the passed UserStore
    this.authStore = authStore;
    this.patientStore = patientStore;
  }

  @observable
  public unschduledAppointments: any = [];

  @observable
  public warningDialogMessage: string = "";

  public setWarningDialogMessage = async (value: string) => {
    this.warningDialogMessage = value;
  };

  public setUnscheduledAppointments = (value: any) => {
    this.unschduledAppointments = value;
  };

  @observable
  public resourceRequest: IPromiseBasedObservable<any> | null = null;

  public createForTreatments = async (
    body: AppointmentTemplateDto,
    isContinuous: boolean = false,
  ) => {
    try {
      const { data } = (await this.apiClient.createForTreatments(body, isContinuous)) as any;
      if (data) {
        notificationStore.showMessage("Behandlungen erfolgreich erstellt!!", "success");
      }
      return data || [];
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "Behandlungen konnten nicht erstellt werden!";

      notificationStore.showMessage(errorMessage, "error", error);
      this.resourceRequest = fromPromise.reject(error);
    }
  };

  @action
  public createAppointmentsForServices = async (body: SalesTemplateDto) => {
    try {
      const { data } = (await this.apiClient.createAppointmentsForServices(body)) as any;
      if (data) {
        notificationStore.showMessage("Behandlungen erfolgreich erstellt!!", "success");
      }
      return data || [];
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "Behandlungen konnten nicht erstellt werden!";

      notificationStore.showMessage(errorMessage, "error", error);
      this.resourceRequest = fromPromise.reject(error);
    }
  };
  public getAppointmentsForPatient = async (patientId: string, addressId?: string) => {
    try {
      const response = await this.apiClient.getAppointmentsForPatient(patientId, addressId);
      const data = response.data as AppointmentDto[];
      return data || [];
    } catch (error) {
      this.resourceRequest = fromPromise.reject(error);
    }
  };

  public getAppointmentsPrintOut = async (id: string, singleRx: boolean) => {
    try {
      const response = await this.apiClient.getAppointmentsPrintOut(id, singleRx);
      if (!response) {
        notificationStore.showMessage("Fehler beim Abrufen des Dokuments", "error");
      }

      return response.data;
    } catch (error) {
      this.resourceRequest = fromPromise.reject(error);
    }
  };

  public getInActiveAppointmentsCount = async (patientId: number) => {
    try {
      const response = await this.apiClient.getInActiveAppointmentsCount(patientId);

      return response.data;
    } catch (error) {
      this.resourceRequest = fromPromise.reject(error);
    }
  };

  public getFilteredAppointmentsForPatient = async (
    patientId: number,
    appointmentQueryFilters: AppointmentQueryFilters,
    omitUnscheduled: boolean,
    addressId?: string,
  ) => {
    try {
      const response = await this.apiClient.getFilteredAppointmentsForPatient(
        patientId,
        appointmentQueryFilters,
        addressId,
        omitUnscheduled,
      );
      const data = response.data as AppointmentDto[];
      return data || [];
    } catch (error) {
      this.resourceRequest = fromPromise.reject(error);
    }
  };

  public getAppointmentsForPatientWithRxId = async (rxId: string) => {
    try {
      const { data } = (await this.apiClient.getAppointmentsForPatientWithRxId(rxId)) as any;
      return data || [];
    } catch (error) {
      this.resourceRequest = fromPromise.reject(error);
    }
  };
  public getAppointmentsRx = async (rxNumber: string) => {
    try {
      const { data } = (await this.apiClient.getAppointmentsForRx(rxNumber)) as any;
      return data || [];
    } catch (error) {
      this.resourceRequest = fromPromise.reject(error);
    }
  };

  public getAppointmentsForTherapyRx = async (therapyRxId: string, isContinuous: boolean) => {
    try {
      const { data } = (await this.apiClient.getAppointmentsForTherapyRx(
        therapyRxId,
        isContinuous,
      )) as any;
      return data || [];
    } catch (error) {
      this.resourceRequest = fromPromise.reject(error);
    }
  };

  public getUnattendedAppointmentsForRx = async (rxId: string) => {
    try {
      const { data } = (await this.apiClient.getUnattendedAppointmentsForRx(rxId)) as any;
      return data || [];
    } catch (error) {
      this.resourceRequest = fromPromise.reject(error);
    }
  };

  // @action
  // public deleteUnattendedAppointments = async (rxId: string) => {
  //   try {
  //     const { data } = (await this.apiClient.deleteUnAttendedAppointmentsForRx(rxId)) as any;
  //     notificationStore.showMessage(data.message,"success")
  //     await this.patientStore.updatePatientData(this.patientStore.filteredPatient);
  //     return data || [];
  //   } catch (error) {
  //     notificationStore.showMessage("Termin konnte nicht gelöscht werden: ", "error", error);
  //     this.resourceRequest = fromPromise.reject(error);
  //   }
  // };

  public setAttended = async (appointmentId: string, isAttended: boolean) => {
    try {
      const { data } = (await this.apiClient.setAttended(appointmentId, isAttended)) as any;
      if (data) {
        notificationStore.showMessage(data, "success");
        if (this.patientStore.selectedPatientId) {
          // await this.patientStore.updatePatientData(this.patientStore.filteredPatient);
        }
        this.setWarningDialogMessage(data);
      }

      return data || [];
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "Fehler beim Festlegen des besuchten Termins.";

      notificationStore.showMessage(errorMessage, "error", error);
    }
  };

  public attendTimeSlot = async (timeSlotId: string, value: boolean) => {
    try {
      const { data } = (await this.apiClient.setTimeSlotAttended(timeSlotId, value)) as any;
      if (data) {
        notificationStore.showMessage(data, "success");
        // if (this.patientStore.filteredPatient) {
        //   await this.patientStore.updatePatientData(this.patientStore.filteredPatient);
        // }
        this.setWarningDialogMessage(data);
      }
      return data || [];
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "Fehler beim Festlegen des besuchten Termins.";

      notificationStore.showMessage(errorMessage, "error", error);
    }
  };
  // @action
  // public getWaitingListByRxSeries = async (date: string, filters: WaitListFilters) => {
  //   try {
  //     const { data } = (await this.apiClient.getWaitingListByRxSeries(date, filters)) as any;
  //     return data || [];
  //   } catch (error: any) {
  //     const errorMessage = error.response?.data?.message || "Fehler beim Abrufen der Warteliste";

  //     notificationStore.showMessage(errorMessage, "error", error);
  //   }
  // };

  public getValueofAppointment = async (id: string): Promise<any> => {
    const { data } = (await this.apiClient.getValueofAppointment(id)) as any;
    return data || [];
  };

  // @action
  // public deleteContinuousAppointment = async (appointmentId: string) => {
  //   try {
  //     const { data } = (await this.apiClient.deleteContinuous(appointmentId)) as any;
  //     return data || [];
  //   } catch (error) {
  //     notificationStore.showMessage("Dauertermin konnte nicht gelöscht werden: ","error", error);
  //     this.resourceRequest = fromPromise.reject(error);
  //   }
  // }
  // public deleteContinuousAppointment = async (appointmentId: string) => {
  //   try {
  //     const { data } = (await this.apiClient.deleteContinuous(appointmentId)) as any;
  //     return data || [];
  //   } catch (error) {
  //     notificationStore.showMessage("Dauertermin konnte nicht gelöscht werden: ", "error", error);
  //     this.resourceRequest = fromPromise.reject(error);
  //   }
  // };

  public getSolutions = async (patientId: string, max: number) => {
    // console.log("Get Solutions in Appointment Store");
    // try {
    const { data } = (await this.apiClient.getSolutions(
      patientId,
      max,
      this.userStore.user?.locationId as string,
    )) as any;
    return data || [];
    // } catch (error) {
    //   this.resourceRequest = fromPromise.reject(error);
    // }
  };

  public getSolution = async (
    patientId: string,
    agentParameters: AppointmentSchedulingParametersDto,
    wishTherapists?: string[],
  ): Promise<SolutionDto | null> => {
    // console.log("Get Solution in Appointment Store");
    try {
      const { data } = (await this.apiClient.getSolution(
        patientId,
        this.userStore.user?.locationId as string,
        agentParameters.searchFunction, // spread individual properties
        agentParameters.pruningTarget,
        undefined, // betaGap is controlled by backend config
        agentParameters.betaTherapists,
        agentParameters.betaTreatmentMonths,
        agentParameters.betaDrivingTime,
        wishTherapists,
      )) as any;
      return data;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || "KI Fehler";
      notificationStore.showMessage(errorMessage, "error", error);
      return null;
    }
  };

  public scheduleAppointmentSuggestionJob = async (
    patientId: string,
    agentParameters: AppointmentSchedulingParametersDto,
    wishTherapists?: string[],
  ): Promise<GeneratedWorkflowDto | null> => {
    // console.log("Get Solution in Appointment Store");
    try {
      const response = await this.apiClient.scheduleJobForAppointmentSuggestion(
        patientId,
        this.userStore.user?.locationId as string,
        agentParameters.searchFunction, // spread individual properties
        agentParameters.pruningTarget,
        undefined, // betaGap is controlled by backend config
        agentParameters.betaTherapists,
        agentParameters.betaTreatmentMonths,
        agentParameters.betaDrivingTime,
        wishTherapists,
      );
      return response.data;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || "KI Fehler";
      notificationStore.showMessage(errorMessage, "error", error);
      return null;
    }
  };

  public runRescheduling = async (
    request: ReschedulingRequestDto,
  ): Promise<GeneratedWorkflowDto | null> => {
    try {
      const response = await this.apiClient.runRescheduling(request);
      // API client types say ReschedulingResultDto, but backend actually returns GeneratedWorkflowDto
      return response.data as unknown as GeneratedWorkflowDto;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || "Rescheduling Fehler";
      notificationStore.showMessage(errorMessage, "error", error);
      return null;
    }
  };

  public saveAppointments = async (appointment: AppointmentDto[]) => {
    try {
      // MD-286: stamp the viewed site onto every slot that has none yet, so
      // slots know which site they were planned at. Already-stamped slots
      // keep their site.
      const locationId = this.userStore.user?.locationId;
      const stamped = locationId
        ? appointment.map((app) => ({
            ...app,
            timeSlots: app.timeSlots?.map((ts: any) =>
              ts.locationId == null ? { ...ts, locationId } : ts
            ),
          }))
        : appointment;
      const { data } = (await this.apiClient.saveAppointments(stamped as AppointmentDto[])) as any;
      notificationStore.showMessage("Termin erfolgreich erstellt", "success");
      return data || [];
    } catch (error: any) {
      this.resourceRequest = fromPromise.reject(error);
      const errorMessage = error.response?.data?.message || "Fehler bei der Terminerstellung";

      notificationStore.showMessage(errorMessage, "error", error);
    }
  };

  public saveSolution = async (solution: SolutionDto): Promise<boolean> => {
    try {
      // MD-286: same site-stamp as saveAppointments — scheduler solutions
      // create slots too.
      const locationId = this.userStore.user?.locationId;
      const stampedSolution = locationId
        ? {
            ...solution,
            appointments: solution.appointments?.map((app) => ({
              ...app,
              timeSlots: app.timeSlots?.map((ts: any) =>
                ts.locationId == null ? { ...ts, locationId } : ts
              ),
            })),
          }
        : solution;
      //save solution is has a void return value
      await this.apiClient.saveSolution(stampedSolution as SolutionDto);
      notificationStore.showMessage("Termin erfolgreich erstellt", "success");
      return true;
    } catch (error: any) {
      this.resourceRequest = fromPromise.reject(error);
      const errorMessage = error.response?.data?.message || "Fehler bei der Terminerstellung";

      notificationStore.showMessage(errorMessage, "error", error);
      // console.log("ERROR saveSolution", error);
      return false;
    }
  };
  public getBlockedSlotsForPatient = async (patientId: number) => {
    try {
      const { data } = (await this.apiClient.getBlockedSlotsForPatient(patientId)) as any;
      return data || [];
    } catch (error) {
      this.resourceRequest = fromPromise.reject(error);
    }
  };
  public saveBlockedSlotForPatient = async (patientId: number, timeSlots: TimeSlotDto[]) => {
    try {
      const { data } = (await this.apiClient.saveBlockedSlotsForPatient(
        patientId,
        timeSlots,
      )) as any;
      return data || [];
    } catch (error: any) {
      this.resourceRequest = fromPromise.reject(error);
      const errorMessage =
        error.response?.data?.message ||
        "Der blockierte Slot für Patient konnte nicht gespeichert werden!";

      notificationStore.showMessage(errorMessage, "error", error);
    }
  };

  public deleteBlockedSlot = async (slotId: string) => {
    try {
      const { data } = (await this.apiClient.deleteBlockedSlot(slotId)) as any;
      return data || [];
    } catch (error) {
      this.resourceRequest = fromPromise.reject(error);
    }
  };
  public getSolutionsForContinuous = async (
    appointmentDto: AppointmentTemplateDto,
    max: number,
  ) => {
    try {
      const { data } = (await this.apiClient.getSolutionsForContinuous(
        max,
        this.userStore.user?.locationId as string,
        appointmentDto,
      )) as any;
      return data || [];
    } catch (error) {
      this.resourceRequest = fromPromise.reject(error);
    }
  };

  public getCancelledAndUnscheduledAppointments = async (rank: boolean) => {
    try {
      if (!this.userStore.user?.locationId) {
        console.warn("Location Id not yet ready");
        return [];
      }
      // console.log("Cancelled Appointmentsdff",(await this.apiClient.getCancelledAppointments(this.userStore.user?.locationId as string)) as any);
      const { data } = (await this.apiClient.getCancelledAndUnscheduledAppointments(
        this.userStore.user?.locationId as string,
        rank,
      )) as any;
      this.setUnscheduledAppointments(data);
      return data || [];
    } catch (error) {
      this.resourceRequest = fromPromise.reject(error);
      notificationStore.showMessage("Konnte stornierte Termine nicht abrufen", "error", error);
    }
  };

  public getCancelledAppointmentsForPatient = async (patientId: number) => {
    try {
      // console.log("Cancelled Appointmentsdff",(await this.apiClient.getCancelledAppointments(this.userStore.user?.locationId as string)) as any);
      const { data } = (await this.apiClient.getCancelledAppointmentsForPatient(patientId)) as any;
      return data || [];
    } catch (error) {
      this.resourceRequest = fromPromise.reject(error);
      notificationStore.showMessage("Konnte stornierte Termine nicht abrufen", "error", error);
    }
  };

  public savePatientUnavailibilityForPatient = async (
    patientId: number,
    patientAvailability: PatientAvailabilityDto[],
  ) => {
    try {
      const { data } = (await this.apiClient.savePatientUnavailibilityForPatient(
        patientId,
        patientAvailability,
      )) as any;
      notificationStore.showMessage("Gespeicherte Patientenverfügbarkeit!", "success");
      return data || [];
    } catch (error: any) {
      this.resourceRequest = fromPromise.reject(error);
      const errorMessage =
        error.response.data.message ||
        "Die Nichtverfügbarkeit des Patienten konnte nicht gespeichert werden";
      // console.log("ERROR", errorMessage, error.response?.message, error.response.data.message);
      notificationStore.showMessage(errorMessage, "error", error);
    }
  };

  public getPatientUnavailabilityForPatient = async (patientId: number) => {
    try {
      const { data } = (await this.apiClient.getPatientUnavailabilityForPatient(patientId)) as any;
      return data || [];
    } catch (error: any) {
      this.resourceRequest = fromPromise.reject(error);
      const errorMessage =
        error.response.data.message ||
        "Die Nichtverfügbarkeit des Patienten konnte nicht geladen werden";
      notificationStore.showMessage(errorMessage, "error", error);
    }
  };
  public deletePatientUnavailability = async (groupId: string) => {
    try {
      const { data } = (await this.apiClient.deletePatientUnavailability(groupId)) as any;
      return data || [];
    } catch (error: any) {
      this.resourceRequest = fromPromise.reject(error);
      const errorMessage =
        error.response.data.message ||
        "Die Nichtverfügbarkeit des Patienten konnte nicht gelöscht werden";
      notificationStore.showMessage(errorMessage, "error", error);
    }
  };

  public backfillContinuousAppointment = async (id: string) => {
    try {
      const { data } = (await this.apiClient.backFillContinuous(id)) as any;
      return data || [];
    } catch (error: any) {
      this.resourceRequest = fromPromise.reject(error);
      const errorMessage = error.response.data.message || "Fehler beim Auffüllen des Dauertermins";
      notificationStore.showMessage(errorMessage, "error", error);
    }
  };

  public copyAppointment = async (appointmentId: string, isContinuous: boolean) => {
    try {
      const { data } = (await this.apiClient.copyToNew(appointmentId, isContinuous)) as any;
      return data || [];
    } catch (error: any) {
      this.resourceRequest = fromPromise.reject(error);
      const errorMessage = error.response.data.message || "Fehler beim Kopieren des Termins";
      notificationStore.showMessage(errorMessage, "error", error);
    }
  };

  public getContinuousAppointmentsForPatient = async (patientId: string, addressId?: string) => {
    try {
      const response = await this.apiClient.getContinuousAppointmentsForPatient(
        patientId,
        addressId,
      );
      const data = response.data as ContinuousAppointmentDto[];
      return data || [];
    } catch (error) {
      this.resourceRequest = fromPromise.reject(error);
      console.error("Error fetching continuous appointments:", error);
      notificationStore.showMessage("Fehler beim Abrufen der Dauertermine", "error", error);
    }
  };
  public getAppointmentsForSeries = async (seriesId: string) => {
    try {
      const { data } = (await this.apiClient.getAppointmentsForSeries(seriesId)) as any;
      return data || [];
    } catch (error) {
      this.resourceRequest = fromPromise.reject(error);
      console.error("Error fetching series appointments:", error);
      notificationStore.showMessage("Fehler beim Abrufen von Serienterminen", "error", error);
    }
  };

  public frequencyValidationForAppointment = async (appointmentId: string, date: string) => {
    try {
      const { data } = (await this.apiClient.frequencyValidationForAppointment(
        appointmentId,
        date,
      )) as any;
      return data || [];
    } catch (error) {
      notificationStore.showMessage("Fehler bei der Frequenzvalidierung des Termins", "error");
      this.resourceRequest = fromPromise.reject(error);
    }
  };

  adjustOutsideAppointments = async (userId: string, startDate: string, endDate: string) => {
    try {
      const jdjustOutsideAppointmentsDto: AdjustOutsideAppointmentsDto = {
        userId,
        start: startDate,
        end: endDate,
      };
      const { data } = (await this.apiClient.adjustOutsideAppointments(
        jdjustOutsideAppointmentsDto,
      )) as any;
      return data || [];
    } catch (error) {
      notificationStore.showMessage(
        "Error on adjusting working hours and gonefishings for outside appointments",
        "error",
      );
      this.resourceRequest = fromPromise.reject(error);
    }
  };
}
